<template>
  <b-card class="border card overflow-hidden rounded shadow mt-4 mt-sm-0 mt-md-4" no-body>
    <b-card-header class="d-flex justify-content-between">
      <h2 class="h5 mb-0">
        {{ $t('Review from {fullName}', { fullName: $t('fullName', {title: lead.enquiry.title, forename: lead.enquiry.forename, surname: lead.enquiry.surname }) }) }}
      </h2>
    </b-card-header>
    <b-card-body>
      <div class="p-2 p-xxl-4 review-card__body">
        <div class="row justify-content-between mb-2">
          <div class="col-6">
            <strong class="d-block">{{ lead.feedback.created_date | parseDate }}</strong>
            <span class="mb-0">
              {{ lead.feedback.is_published ? $t('Published') : $t('Unpublished') }}
              <font-awesome-icon v-b-tooltip.hover="tooltip" icon="info-circle" />
            </span>
          </div>
          <div class="col-6 align-items-center customer__feedback d-flex flex-row flex-wrap justify-content-end">
            <b-form-rating
              v-model="rating"
              class="b-rating-feedback"
              no-border
              precision="2"
              readonly
            />
          </div>
        </div>
        <div class="row">
          <div class="col">
            <p>{{ lead.feedback.work_done_comments }}</p>
          </div>
        </div>
        <div class="row">
          <div class="col">
            <div class="row">
              <div v-if="responseTs === null || canEditReply" class="col">
                <validation-observer v-slot="{validate, reset, dirty, invalid}">
                  <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required|length:500">
                    <h2 class="font-size-14">
                      <font-awesome-icon :icon="['fas','reply']" rotation="180" />
                      {{ canEditReply ? 'Reply to this review' : 'Reply submitted' }}
                      <span v-if="dirty && canEditReply" class="text-danger">{{ $t('Editing...') }}</span>
                    </h2>
                    <b-form-textarea
                      v-model="response"
                      rows="4"
                      class="border h5 p-3 mb-2"
                      placeholder="Enter your response here"
                      :readonly="!canEditReply"
                      :state="(dirty || validated) ? (valid ? null : false) : null"
                    />
                    <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </validation-provider>
                  <div class="row">
                    <div class="col-12">
                      <div :class="['d-flex', 'justify-content-end', 'flex-row', 'py-2', {'px-2 response-edit rounded border': canEditReply}]">
                        <div v-if="canEditReply" class="w-60 font-weight-600 d-flex align-items-center font-size-13">
                          {{ editMessage }}
                        </div>
                        <div class="w-40 d-flex justify-content-end">
                          <b-button
                            v-if="responseTs === null"
                            class="btn-lead-view"
                            size="sm"
                            @click="onSubmitResponse(validate, reset, false)"
                          >
                            {{ $t('Reply') }}
                          </b-button>
                          <b-button
                            v-else
                            class="btn-reply align-self-center"
                            size="sm"
                            :disabled="busy"
                            @click="editSubmittedResponse(validate, reset, dirty, invalid, true)"
                          >
                            {{ canEditReply ? $t('Edit Reply') : $t('Submit reply') }}
                          </b-button>
                        </div>
                      </div>
                    </div>
                  </div>
                </validation-observer>
              </div>
              <div v-else class="col">
                <h2 class="font-size-14">
                  <font-awesome-icon :icon="['fas','reply']" rotation="180" />
                  {{ $t('Reply submitted - {when}', {when: $d(lead.feedback.response_ts, 'shortWithTime')}) }}
                </h2>
                <div class="border responded__container">
                  <div class="p-3 w-100 col-12 col-md responded__message">
                    <div>{{ lead.feedback.response_ts | parseDateTime }}</div>
                    <p class="text-break">
                      {{ lead.feedback.response ? lead.feedback.response : response }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import dates from "@/services/date";

import {ValidationObserver, ValidationProvider} from 'vee-validate';
import notification from "@/imports/mixins/notification";

export default {
  name: "LeadReview",
  components: {ValidationObserver, ValidationProvider},
  mixins: [notification],
  props: ['lead'],
  data: function() {
    return {
      response: this.lead.feedback.response,
      busy: false,
      responseTs: this.lead.feedback.response_ts ? new Date(this.lead.feedback.response_ts) : null,
      editTime: null,
      rating: (this.lead.feedback.rating_overall / 2),
    }
  },
  computed: {
    editMessage: function() {
      if (this.responseTs === null) {
        return null;
      } else if (this.canEditReply) {
        return this.$t(`You have {timeToEdit} to edit this reply.`, { timeToEdit: this.timeToEdit});
      }
      return this.$t("You can no longer edit this reply");
    },
    canEditReply: function() {
      return (this.responseTs === null || dates.diffMinutes(new Date(), this.responseTs) < 60);
    },
    timeToEdit: function() {
      return this.responseTs ? dates.timeToNow(dates.addTime(this.responseTs, {minutes:60})) : null;
    },
    tooltip: function() {
      return {
        title: (this.lead.feedback.is_published) ? this.publishedTooltip : this.unpublishedTooltip,
        html: true,
      }
    },
    publishedTooltip: function() {
      return this.$t("This feedback has been posted on your public profile. Great feedback improves your lead acceptance" +
        " score and allows us to send you more work.");
    },
    unpublishedTooltip: function() {
      return this.$t("This feedback has not been posted on your public profile. We only publish feedback for jobs where" +
        " you have quoted, the work has been carried out and the customer has left feedback.");
    },
  },
  methods: {
    onSubmitResponse: function(validate, reset, isEdit) {
      this.validateSubmission(validate, reset, isEdit);
    },
    editSubmittedResponse: function(validate, reset, dirty, invalid, isEdit) {
      if (this.canEditReply) {
        this.validateSubmission(validate, reset, isEdit);
      }
    },
    validateSubmission: function(validate, reset, isEdit) {
      validate().then((success) => {
        if (success) {
          this.busy = true
          this.$api.feedback.respond(this.lead.id, this.response)
            .then(() => {
              this.responseTs = this.responseTs ?? new Date();
              this.notify(
                this.$bvToast,
                this.$t('Your reply has been {updatedOrSent}', { updatedOrSent: isEdit ? 'updated' : 'sent' }),
                null,
                'success'
              );
            })
            .finally(() => {
              this.busy = false;
              reset();
            });
        }
      });
    }
  }
}
</script>

<style scoped lang="scss">
.review-card {
  &__body {
    font-size: .813rem;
  }
}
::v-deep .customer__feedback {
  .b-rating-feedback {
    font-size: 1.5em;
    padding: 0;
    max-width: 125px;
    height: auto;

    .b-rating-star {
      padding: 0 .1rem;
      color: $theme-rating-color;
    }
  }
}

.response {
  @include media-breakpoint-up(md) {
    &:before {
      content: '';
      height: 100%;
      position: absolute;
      width: 3px;
      background: #fef3cc;
      left: 0;
    }
  }

  .form-control {
    font-size: $font-size-14;
  }

  &-edit {
    background: #fef3cc;
  }
}

.responded {
  &__container {
    background: $feedback-message-textarea-bg;
    border-radius: $border-radius-sm;
    height: 8rem;
  }

  &__message {
    height: 6.825rem;
    overflow-y: auto;
  }
}
</style>
